import { useState } from "react";

import { makePostApiCall, makePutApiCall } from "../../../shared/makeApiCall";
import { useToast } from "../../../contexts/ToastContext";

import DeleteConfirmationForm from "../../../components/forms/DeleteConfirmationForm";
import ModelErrorComponent from "../../../components/modals/ModelErrorComponent";
import type { EmptyModalState } from "../../../components/modals/EmptyModal";
import type { CustomerGroup, CustomerGroupFormValues, CustomerGroupMutationResponse } from "../../../types/customerGroups";

export function CustomerGroupDetailsViewModel(logout: () => void, groupId?: string) {
  const { showSuccess } = useToast();
  const [emptyModal, setEmptyModelOpen] = useState<EmptyModalState>({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [group, setGroup] = useState<CustomerGroup | null>(null);

  const showErrorModal = (title: string, subtitle?: string) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent setOpen={setEmptyModelOpen} subtitle={subtitle} title={title} />
      ),
      open: true,
    });
  };

  const getGroup = async function (search: string | null = null, page = 1) {
    setLoading(true);
    const payload = {
      page: page,
      search: search,
    };
    const callResult = await makePostApiCall<CustomerGroup, typeof payload>(`customerGroups/${groupId}`, payload);
    setLoading(false);
    if (callResult.success) {
      setGroup(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch group");
    }
  };
  const createGroup = async function (values: CustomerGroupFormValues) {
    const callResult = await makePostApiCall<CustomerGroupMutationResponse, CustomerGroupFormValues>(`createCustomerGroup`, values, "multipart/form-data");

    if (callResult.success) {
      showSuccess(callResult.data.message || "Customer Group created");
      window.location.assign("/customer-groups/" + callResult.data.id);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not create group");
    }
  };

  const updateGroup = async function (values: CustomerGroupFormValues) {
    const callResult = await makePostApiCall<CustomerGroupMutationResponse, CustomerGroupFormValues>(
      `updateCustomerGroups/${groupId}`,
      values,
      "multipart/form-data"
    );

    if (callResult.success) {
      showSuccess(callResult.data.message || "Customer Group updated");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not update group");
    }
  };

  return {
    createGroup,
    updateGroup,
    getGroup,
    group,
    loading,
    emptyModal,
    setEmptyModelOpen,
  };
}
